import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEOHead from "@/components/SEOHead";
import FaqSection from "@/components/FaqSection";
import { SERVICES } from "@/lib/services";
import { SERVICE_PHOTOS, photoDims } from "@/lib/photos";
import { SERVICES_FAQS } from "@/lib/faqs";

// SEO-optimized alt text per service — applied only to service rows whose
// physical image asset matches an image the SEO provider identified.
// Rows not listed keep the default alt (service title).
const SERVICE_ALT_TEXT = {
    "custom-homes": "Custom-built home in Tahlequah, OK by Harris Homes",
    "residential-construction": "Modern residential construction project featuring custom home exterior in Tahlequah OK",
    "craftsmanship": "Custom carpentry and built-in wood finishes completed by Harris Homes in Tahlequah Oklahoma",
};

export default function Services() {
 return (
 <div data-testid="services-page">
 <SEOHead
 title="Home Building & Remodeling Services in Tahlequah, OK"
 description="Custom homes, additions and remodels in Tahlequah, OK. Harris Homes handles design, permits and construction. Call 918-373-4341."
 canonical="https://harrishomesok.com/services"
 />
 {/* Header */}
 <section className="pt-20 md:pt-32 pb-16 md:pb-20 px-6 md:px-10">
 <div className="max-w-[1480px] mx-auto">
 <Reveal>
 <div className="overline mb-6">Services</div>
 <h1 className="font-display text-5xl sm:text-7xl md:text-[7.5rem] leading-[0.95] tracking-tight max-w-5xl">
 Home building and remodeling
 <br />
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 in Tahlequah.
 </span>
 </h1>
 <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
 From new custom homes to careful renovations, we take a small
 number of projects each year and run every one with personal
 attention from start to finish. Learn more about our approach as
 a{" "}
 <Link
 to="/custom-home-builder-tahlequah-ok"
 data-testid="services-tahlequah-link"
 className="underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]"
 style={{ color: "var(--hh-gold)" }}
 >
 custom home builder in Tahlequah, Oklahoma
 </Link>
 .
 </p>
 </Reveal>
 </div>
 </section>

 {/* Service rows, alternating */}
 <section className="px-6 md:px-10 pb-16">
 <div className="max-w-[1480px] mx-auto space-y-24 md:space-y-32">
 {SERVICES.map((s, i) => {
 const reversed = i % 2 === 1;
 const img = SERVICE_PHOTOS[s.id];
 return (
 <Reveal key={s.id}>
 <div
 data-testid={`service-row-${s.id}`}
 className={`grid md:grid-cols-12 gap-10 md:gap-16 items-center ${
 reversed ? "md:[direction:rtl]" : ""
 }`}
 >
 <div className="md:col-span-6 md:[direction:ltr]">
 <div className="relative img-zoom">
 <img
 src={img}
 alt={SERVICE_ALT_TEXT[s.id] || s.title}
 width={photoDims(img)?.w}
 height={photoDims(img)?.h}
 className="w-full h-[460px] md:h-[560px] object-cover"
 loading="lazy"
 />
 </div>
 </div>
 <div className="md:col-span-6 md:[direction:ltr]">
 <div className="flex items-center gap-3 mb-5">
 <s.Icon size={22} strokeWidth={1.2} style={{ color: "var(--hh-gold)" }} />
 <div className="overline">0{i + 1} · Service</div>
 </div>
 <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
 {s.title}
 </h2>
 <div className="gold-rule w-24 my-6" />
 <p className="text-base md:text-lg leading-relaxed max-w-xl" style={{ color: "var(--hh-text-dim)" }}>
 {s.description}
 </p>
 <ul className="mt-8 space-y-3 max-w-md">
 {["Personal builder on every project", "Honest budgets & timelines", "Quality materials and finishes"].map(
 (item) => (
 <li key={item} className="flex items-start gap-3 text-sm" style={{ color: "var(--hh-text)" }}>
 <Check size={16} strokeWidth={1.6} style={{ color: "var(--hh-gold)", marginTop: 2 }} />
 <span>{item}</span>
 </li>
 )
 )}
 </ul>
 <div className="mt-10 flex flex-wrap items-center gap-5">
 <Link to="/contact" data-testid={`service-row-cta-${s.id}`} className="btn-ghost">
 Discuss This Project <ArrowRight size={14} />
 </Link>
 {s.link && (
 <Link
 to={s.link.href}
 data-testid={`service-row-link-${s.id}`}
 className="text-sm underline underline-offset-4 decoration-1 transition-colors hover:text-[var(--hh-gold-light)]"
 style={{ color: "var(--hh-gold)" }}
 >
 More about {s.link.label}
 </Link>
 )}
 </div>
 </div>
 </div>
 </Reveal>
 );
 })}
 </div>
 </section>

 {/* Shop houses & barndominiums */}
 <section className="px-6 md:px-10 pb-24 md:pb-32">
 <div className="max-w-[1480px] mx-auto">
 <Reveal>
 <div
 data-testid="shop-house-block"
 className="border p-8 md:p-14 grid md:grid-cols-12 gap-8 items-center"
 style={{ borderColor: "var(--hh-border)", background: "var(--hh-bg-2)" }}
 >
 <div className="md:col-span-8">
 <div className="overline mb-5">Also Built Here</div>
 <h2 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
 Shop Houses and Barndominiums
 </h2>
 <div className="gold-rule w-24 my-6" />
 <p className="text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: "var(--hh-text-dim)" }}>
 A shop house pairs a real workshop or garage with finished
 living space under one roof; a barndominium does the same with
 barn-style construction. We design and build both on your land
 in Tahlequah and across Cherokee County.
 </p>
 </div>
 <div className="md:col-span-4 md:text-right">
 <Link
 to="/shop-house-barndominium-builder-tahlequah-ok"
 data-testid="shop-house-block-link"
 className="btn-gold"
 >
 Shop House Builder <ArrowRight size={14} />
 </Link>
 </div>
 </div>
 </Reveal>
 </div>
 </section>

 {/* FAQ */}
 <FaqSection
 overline="Frequently Asked"
 headingLead="More about our"
 headingItalic="services."
 items={SERVICES_FAQS}
 testId="services-faq"
 />

 {/* CTA */}
 <section className="py-24 md:py-32 px-6 md:px-10 text-center" style={{ background: "var(--hh-bg-2)" }}>
 <Reveal>
 <h2 className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto">
 Don't see exactly what you need?
 <br />
 <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}>
 Let's talk anyway.
 </span>
 </h2>
 <p className="mt-6 max-w-xl mx-auto text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
 Most great projects start as a conversation. Tell us what you're
 thinking, we'll be honest about whether we're the right builder
 for it.
 </p>
 <div className="mt-10">
 <Link to="/contact" data-testid="services-cta-button" className="btn-gold">
 Start a Conversation <ArrowRight size={14} />
 </Link>
 </div>
 </Reveal>
 </section>
 </div>
 );
}
